import "./tournament-message-details.scss";
import { useCallback, useEffect } from "react";
import {
  GSEmptyList,
  GSForm,
  GSSidePanelPage,
} from "golfstatus_react_components";
import {
  faChevronLeft,
  faClock,
  faPaperPlane,
  faPen,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { useMatch, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getMessage,
  selectCurrentMessage,
  selectCurrentMessageRecipientGroups,
  selectCurrentMessageRecipientCount,
  selectMessagesLoading,
  selectMessagesNotifications,
  selectSelectedMessageTournaments,
  clearNotifications,
  clearCurrentMessage,
  cancelMessage,
} from "../../../reducers/messagesSlice";
import { selectCurrentTournament } from "../../../reducers/tournamentSlice";
import { useNotificationBanner } from "../../../hooks/notificationHooks";
import { messageDetailsForm } from "../../../forms/MessageForms";

const TournamentMessageDetails = () => {
  const navigate = useNavigate();
  const match = useMatch("/:tournamentID/messages/:messageID");
  const dispatch = useDispatch();
  const currentMessage = useSelector(selectCurrentMessage);
  const currentTournament = useSelector(selectCurrentTournament);
  const recipientGroups = useSelector(selectCurrentMessageRecipientGroups);
  const selectedTournaments = useSelector((state) =>
    selectSelectedMessageTournaments(
      state,
      match?.params?.tournamentID,
      currentTournament,
    ),
  );
  const recipientCount = useSelector(selectCurrentMessageRecipientCount);
  const loading = useSelector(selectMessagesLoading);
  const notifications = useSelector(selectMessagesNotifications);

  useEffect(() => {
    dispatch(clearCurrentMessage());
    dispatch(
      getMessage({
        tournamentID: match?.params?.tournamentID,
        messageID: match?.params?.messageID,
      }),
    );
  }, [dispatch, match?.params?.tournamentID, match?.params?.messageID]);

  const goBack = useCallback(() => {
    dispatch(clearNotifications());
    navigate(`/${match?.params?.tournamentID}/messages`);
  }, [dispatch, navigate, match?.params?.tournamentID]);

  const handleSend = () => {
    navigate("send-confirmation");
  };

  const handleDelete = () => {
    navigate("remove");
  };

  const cancelSend = () => {
    dispatch(
      cancelMessage({
        tournamentID: match?.params?.tournamentID,
        messageID: match?.params?.messageID,
      }),
    );
  };

  const displayStatus = currentMessage?.status;

  const selectedGroups = recipientGroups.filter(
    (g) => g.includeAll === true || (g?.selectedCount ?? 0) > 0,
  );

  const isLocked = currentMessage?.status === "sent";
  const canSend =
    (recipientCount > 0 || currentMessage?.selectedGroupsCount > 0) &&
    (currentMessage?.subject ||
      currentMessage?.bodyHtml ||
      currentMessage?.bodyText);

  const getNavigation = () => ({
    title: "Message Details",
    leftIcon: faChevronLeft,
    leftButtonClick: goBack,
  });

  const getRecipientsActions = () => {
    if (isLocked) return [];
    return [
      {
        actionIcon: faPen,
        actionClick: () => navigate("recipients"),
        type: "light-grey",
      },
    ];
  };

  const getContentActions = () => [
    {
      actionIcon: faPen,
      actionClick: () => {
        dispatch(clearNotifications());
        navigate("edit");
      },
      type: "light-grey",
    },
  ];

  const getEmptyRecipientsMessage = () => (
    <GSEmptyList
      title="No Recipients"
      detail="Select who you want to receive this message."
      actions={[
        {
          title: "Select Recipients",
          buttonIcon: faUsers,
          onClick: () => navigate("recipients"),
          type: "black",
        },
      ]}
    />
  );

  const getEmptyContentMessage = () => (
    <GSEmptyList
      title="No Message Content"
      detail="Start crafting your message."
      actions={[
        {
          title: "Compose Message",
          buttonIcon: faPen,
          onClick: () => navigate("edit"),
          type: "black",
        },
      ]}
    />
  );

  const getDrawerActions = () => {
    if (currentMessage?.status === "scheduled") {
      return [
        {
          name: "Cancel Send",
          action: cancelSend,
          buttonIcon: faClock,
          type: "light-grey",
        },
      ];
    }
    return [
      {
        name: "Send Now",
        action: handleSend,
        buttonIcon: faPaperPlane,
        isDisabled: !canSend,
        type: "black",
      },
      {
        name: "Send Later",
        action: () => navigate("schedule"),
        buttonIcon: faClock,
        isDisabled: !canSend,
        type: "light-grey",
      },
    ];
  };

  const getDrawer = () => ({ actions: getDrawerActions() });

  const timeoutAction = () => {
    dispatch(clearNotifications());
  };

  const [banner] = useNotificationBanner({ notifications, timeoutAction });

  const onViewRecipientGroup = (item) => {
    if (!item?.recipientGroupId) return;
    navigate(`recipients/${item.recipientGroupId}/view`);
  };

  const formContext = {
    selectedTournaments,
    recipientGroups,
    selectedGroups,
    recipientCount,
    displayStatus,
    isLocked,
    getRecipientsActions,
    getContentActions,
    getEmptyRecipientsMessage,
    getEmptyContentMessage,
    onViewRecipientGroup,
    onComposeMessage: () => navigate("edit"),
    onDeleteMessage: handleDelete,
    onSelectRecipients: () => navigate("recipients"),
  };

  return (
    <tournament-message-details>
      <GSSidePanelPage
        header={getNavigation()}
        banner={banner}
        loading={loading?.length > 0}
        loadingText="Loading Message..."
        content={
          <GSForm
            formSections={messageDetailsForm(formContext, currentMessage)}
          />
        }
        drawer={isLocked ? "" : getDrawer()}
      />
    </tournament-message-details>
  );
};

export default TournamentMessageDetails;
